import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Card, CardBody } from "reactstrap";
// import Loader from "Components/Common/Loader";
import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import ActionMenu from "Components/Common/ActionMenu";
import Icon from "Components/Icons/Icon";
import { useAppDispatch } from "Components/Hooks/hooks";
import { createSelector } from "reselect";
import { selectItemsState } from "slices/items/reducer";
import { useSelector } from "react-redux";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import { deleteItem, getItems } from "slices/thunks";

interface ItemsTableProps {
  isLoadingData?: boolean;
  onAddNewItem: () => void;
  onImportItem: () => void;
  onEditItem: (item: any) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const ItemsTable = ({
  onAddNewItem,
  onEditItem,
  onImportItem,
  currentPage,
  setCurrentPage,
}: ItemsTableProps) => {
  const dispatch = useAppDispatch();
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);

  const itemSelector = createSelector(selectItemsState, (state) => ({
    items: state.items,
    isLoading: state.status.fetch,
    error: state.errors.fetch,
    meta: state.pagination,
  }));

  const { items, isLoading, meta } = useSelector(itemSelector);

  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    const params = searchTerm ? { search: searchTerm } : { page: currentPage };
    dispatch(getItems(params));
  }, [currentPage, dispatch, searchTerm]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns = useMemo(
    () => [
      {
        header: "ITEM",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue()}
            </span>
          );
        },
      },
      {
        header: "CATEGORIES",
        accessorKey: "category_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "SUBCATEGORIES",
        accessorKey: "subcategory_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "UNIT",
        accessorKey: "unit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "PRICE/UNIT",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "UNIT COST",
        accessorKey: "cost",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "ACTION",
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <ActionMenu
              onEdit={() => onEditItem(rowData)}
              onDelete={() => setDeleteItemId(rowData.id)}
            />
          );
        },
      },
    ],
    [onEditItem],
  );

  return (
    <React.Fragment>
      <DeleteItemModal
        show={deleteItemId !== null}
        onCloseClick={() => setDeleteItemId(null)}
        onDeleteClick={() => {
          if (deleteItemId) {
            dispatch(deleteItem(deleteItemId));
          }
          setDeleteItemId(null);
        }}
      />
      <Card id="membersTable" className="br-10 bg-white">
        <CardBody className="pt-0">
          <TableContainer
            columns={columns}
            data={items ?? []}
            isGlobalFilter={true}
            divClass="table-responsive table-card mb-2 bg-white"
            tableClass="align-middle table-nowrap table-borderless category-table"
            thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
            theadClass="table-light"
            SearchPlaceholder="Search item"
            onSearch={handleSearch}
            onPageChange={handlePageChange}
            customPageSize={meta?.per_page}
            totalPages={meta?.last_page}
            currentPage={meta?.current_page}
            fromPage={meta?.from}
            toPage={meta?.to}
            totalItems={meta?.total}
            searchAlign="left"
            rightContent={
              <div className="d-flex gap-2">
                <CustomButton
                  text="New Item"
                  leftIcon={
                    <i className="ri-add-line me-1 align-bottom fs-5" />
                  }
                  className="bg-primary br-7"
                  onClick={onAddNewItem}
                />
                <CustomButton
                  text="Import"
                  leftIcon={<Icon name="Import" />}
                  className="bg-blue059 br-7"
                  onClick={onImportItem}
                />
              </div>
            }
            isInitialLoading={isLoading === "loading" && !items?.length}
            isPaginationLoading={isLoading === "loading" && items?.length > 0}
          />
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default ItemsTable;
